/**
 * SummaryTile component for Infrastructure Map
 *
 * Displays a collapsed node's children as an "n Children" summary.
 * Shows count of hidden endpoints and external link indicators.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { CanvasPosition, SummaryTileData } from '../../../types/infrastructure-map'
import {
  ExternalLinkIndicator,
  TileBackground,
  TileHitArea,
  TileLabels,
} from './summary-tile/summary-tile.parts'

interface SummaryTileProps {
  data: SummaryTileData
  position: CanvasPosition
  onClick: () => void
  hasExternalLinks: boolean
}

export function SummaryTile({
  data,
  position,
  onClick,
  hasExternalLinks,
}: SummaryTileProps): ReactElement {
  const { t } = useTranslation('cases')

  const handleClick = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    onClick()
  }

  const displayCount = data.isRecursive ? data.totalDescendantCount : data.directChildCount

  return (
    <g
      className="summary-tile cursor-pointer"
      onClick={handleClick}
      style={{ pointerEvents: 'auto' }}
    >
      <TileBackground position={position} />
      <TileLabels position={position} displayCount={displayCount} t={t} />
      {hasExternalLinks && <ExternalLinkIndicator position={position} />}
      <TileHitArea position={position} displayCount={displayCount} t={t} />
    </g>
  )
}

export default SummaryTile
